import type { IconSizeKey } from "../../tokens";
import type { TypographyProps } from "../typography";
import type { AvatarSize, AvatarVariant } from "./types";

type TypographyColor = NonNullable<TypographyProps["color"]>;
type TypographyVariant = NonNullable<TypographyProps["variant"]>;

export function generateInitials(name?: string): string {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0]!.substring(0, 2).toUpperCase();
  }
  return ((parts[0]![0] || "") + (parts[parts.length - 1]![0] || "")).toUpperCase();
}

export const avatarBackgroundClass: Record<AvatarVariant, string> = {
  // Holds the circle while the photo loads (native shows no fallback until an error)
  image: "bg-luce-neutral-background-attenuated",
  primary: "bg-luce-brand-primary",
  primaryInverted: "bg-luce-brand-primarySurface",
  anonymous: "bg-luce-neutral-background-attenuated",
  initial: "bg-luce-brand-primary",
  initialGrey: "bg-luce-neutral-background-attenuated",
  initialInverted: "bg-luce-brand-primarySurface",
};

/** Icon size keys: 16/24/24/32/40, and one icon step up on desktop web */
export const avatarIconSize: Record<AvatarSize, IconSizeKey> = {
  xs: "sm",
  sm: "lg",
  md: "lg",
  lg: "2xl",
  xl: "3xl",
};

/** Presence dot 6/10/10/12/16, the same at every width */
export const avatarIndicatorSizeClass: Record<AvatarSize, string> = {
  xs: "size-presence-dot-xs",
  sm: "size-presence-dot-sm",
  md: "size-presence-dot-md",
  lg: "size-presence-dot-lg",
  xl: "size-presence-dot-xl",
};

// The composites nearest Figma's initials (10/12, 12/16, 16/24, 16/24 Medium;
// 24/32 SemiBold): no label or caption style is 12/16 or 24/32
export const avatarInitialsVariant: Record<AvatarSize, TypographyVariant> = {
  xs: "caption-md-medium",
  sm: "body-sm-medium",
  md: "label-md",
  lg: "label-md",
  xl: "heading-h2",
};

// heading-h2 grows to 40/52 on desktop; xl initials stay 24/32 in the 64px box
export const avatarInitialsClass: Partial<Record<AvatarSize, string>> = {
  xl: "md:text-heading-h2",
};

type IconVariant = Extract<AvatarVariant, "primary" | "primaryInverted" | "anonymous">;

export const avatarIconColorClass: Record<IconVariant, string> = {
  primary: "text-luce-brand-onPrimary",
  primaryInverted: "text-luce-brand-onPrimarySurface",
  anonymous: "text-luce-neutral-foreground-attenuated",
};

type InitialsVariant = Extract<AvatarVariant, "initial" | "initialGrey" | "initialInverted">;

export const avatarInitialsColor: Record<InitialsVariant, TypographyColor> = {
  initial: "brand-on-primary",
  initialGrey: "neutral-foreground-attenuated",
  initialInverted: "brand-on-primary-surface",
};
